import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { supabase } from "@/integrations/supabase/client";
import {
  Camera,
  Smartphone,
  Users,
  FileSignature,
  Mail,
  Star,
  Check,
  X,
  Calendar,
  TrendingUp,
  MessageSquare,
  Sparkles,
  CreditCard,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BRAND_COPY, useTradeMode, getPrimaryTrade } from "@/config/trade";
import { GoogleReviewsWidget } from "@/components/GoogleReviewsWidget";

const trades = [
  { slug: "hvac", name: "HVAC" },
  { slug: "plumbing", name: "Plumbing" },
  { slug: "electrical", name: "Electrical" },
  { slug: "roofing", name: "Roofing" },
  { slug: "handyman", name: "Handyman" },
  { slug: "landscaping", name: "Landscaping" },
];

const features = [
  {
    icon: Camera,
    title: "AI Quotes from Job Photos",
    desc: "Customer snaps a photo — you send a quote in 60 seconds.",
  },
  {
    icon: Smartphone,
    title: "Professional Mobile Website + SEO",
    desc: "A fast, mobile-ready site that ranks on Google and books jobs for you.",
  },
  {
    icon: FileSignature,
    title: "Digital Contracts with Signatures",
    desc: "Send it, they sign on their phone — done before you leave the driveway.",
  },
  {
    icon: Users,
    title: "Easy CRM & Customer Tracking",
    desc: "Every lead, quote, and job in one simple list — nothing slips through.",
  },
  {
    icon: MessageSquare,
    title: "Automatic Emails & Text Reminders",
    desc: "Follow-ups go out on autopilot so you never miss a callback.",
  },
  {
    icon: Star,
    title: "Google Reviews Built In",
    desc: "One-click review requests after every job — more 5-stars, more calls.",
  },
];

const tiers = [
  {
    name: "Starter",
    priceMonthly: 497,
    priceAnnual: 397,
    annualBilled: 4764,
    annualSavings: 1200,
    desc: "1–2 users · 75 AI quotes/mo. Best for new & solo owners.",
    bullets: ["Up to 2 users", "75 AI quotes / month", "Email support"],
    cta: "Start Free AI Onboarding",
    href: "/admin/onboarding",
    badge: "Most Popular for New Businesses",
    highlight: true,
    note: "New Business Special: First 3 months at $397/mo (even monthly)",
    monthlyPriceId: "starter_monthly",
    annualPriceId: "starter_annual",
  },
  {
    name: "Pro",
    priceMonthly: 697,
    priceAnnual: 558,
    annualBilled: 6696,
    annualSavings: 1680,
    desc: "Growing crews ready to scale lead flow and follow-ups.",
    bullets: ["Up to 6 users", "300 AI quotes / month", "Priority email support"],
    cta: "Start Free AI Onboarding",
    href: "/admin/onboarding",
    highlight: false,
    monthlyPriceId: "pro_monthly",
    annualPriceId: "pro_annual",
  },
  {
    name: "Scale",
    priceMonthly: 997,
    priceAnnual: 798,
    annualBilled: 9576,
    annualSavings: 2388,
    desc: "Multi-truck operations that want white-glove care.",
    bullets: [
      "Unlimited users",
      "Unlimited AI quotes",
      "White-glove onboarding call",
      "Dedicated Slack channel",
    ],
    cta: "Start Free AI Onboarding",
    href: "/admin/onboarding",
    highlight: false,
    monthlyPriceId: "scale_monthly",
    annualPriceId: "scale_annual",
  },
];

const faqs = [
  {
    q: "Do I need to be good with computers?",
    a: "Nope. If you can use a smartphone, you can use this. We set everything up for you. Your team logs in just like checking email.",
  },
  {
    q: "How is this different from Jobber or ServiceTitan?",
    a: "Those tools charge you per technician — so when you grow, your bill grows. We charge one flat price. Plus we give you the website, AI quoting, and contracts all in one place.",
  },
  {
    q: "Can my customers really get a quote from a photo?",
    a: "Yes. They take a picture of the AC, the leak, the roof, whatever. The AI gives a Good / Better / Best estimate in about a minute. You set the final price.",
  },
  {
    q: "What if I already use another CRM?",
    a: "We can sync with GoHighLevel and pull in your contacts. Most contractors switch over in a weekend and never look back.",
  },
  {
    q: "How fast can I be up and running?",
    a: "Hosted plans go live same-day. If you want us to brand everything and train your team, we do that in about two weeks.",
  },
  {
    q: "Is there a contract or setup fee?",
    a: "No long-term contract. Cancel anytime. The Done-For-You setup is a one-time fee, or you can go month-to-month on any hosted plan.",
  },
];

const Index = () => {
  const [annual, setAnnual] = useState(true);
  const { openCheckout, loading } = usePaddleCheckout();
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const mode = useTradeMode();
  const primaryTrade = getPrimaryTrade();
  const copy =
    primaryTrade && mode !== "all"
      ? BRAND_COPY[mode]
      : {
          eyebrow: "Built for Home Service Contractors",
          headline: "Stop Paying $300–$500 Per Tech for Jobber or ServiceTitan",
          sub: "Get a professional website, AI quotes from job photos, easy CRM, and digital contracts customers can sign on their phone — all for one flat monthly price. Built for HVAC, plumbing, electrical, roofing, and home service companies.",
        };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email);
      setUserId(data.user?.id);
    });
  }, []);

  const subscribe = (priceId: string) => {
    if (!userId) {
      window.location.href = `/admin/login?next=checkout&price=${encodeURIComponent(priceId)}`;
      return;
    }
    openCheckout({
      priceId,
      customerEmail: userEmail,
      customData: { userId },
      successUrl: `${window.location.origin}/checkout/success`,
    });
  };

  const seoTitle = primaryTrade
    ? `${primaryTrade.title} | ProTradeOS`
    : "ProTradeOS — Contractor Software with AI Quotes, CRM & E-Sign";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        seo={{
          title: seoTitle,
          description:
            primaryTrade?.metaDescription ??
            "Stop overpaying for contractor software. One flat price gets you a website, AI photo quotes, customer tracking, and digital contracts. Built for HVAC, plumbing, electrical & roofing.",
          keywords: [
            "contractor software",
            "HVAC CRM",
            "plumbing software",
            "electrical CRM",
            "AI photo quote",
            "Jobber alternative",
            "Housecall Pro alternative",
            "ServiceTitan alternative",
          ],
        }}
        path="/"
      />
      <Navigation />

      {/* HERO */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-sm font-semibold text-primary mb-4 tracking-wider uppercase">
            Built for Home Service Contractors
          </p>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground leading-tight">
            Stop Overpaying Jobber or ServiceTitan
          </h1>
          <p className="text-lg md:text-xl text-gray-700 font-medium mb-8 leading-relaxed max-w-3xl mx-auto">
            AI Photo Quotes + Pro Mobile Website + Full CRM for one flat monthly price. Most owners with 5–15 techs save <span className="font-bold text-primary">$1,000–$4,000+/month</span> vs what they&apos;re paying now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="text-lg px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/ai-quote">
                <Camera className="w-5 h-5 mr-2" /> Try AI Photo Quote — It&apos;s Free
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto border-accent text-accent hover:bg-accent hover:text-accent-foreground"
            >
              <Link to="/pricing">
                See Plans
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-3">
              See why owners are switching to ProTradeOS
            </h2>
            <p className="text-base md:text-lg text-gray-700 font-medium">
              Same features. A fraction of the cost. No per-tech penalty.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
            <table className="w-full text-sm md:text-base">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-4 font-bold text-foreground">Feature</th>
                  <th className="text-center p-4 font-bold text-gray-700">Jobber</th>
                  <th className="text-center p-4 font-bold text-gray-700">ServiceTitan</th>
                  <th className="text-center p-4 font-bold text-primary bg-primary/10">ProTradeOS</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { f: "Monthly Price", j: "$169–$599+ (5–15 users)", s: "$245–$500 per tech", p: "$397–$997 flat" },
                  { f: "Users Included", j: "5 / 10 / 15", s: "Per tech", p: "High / Unlimited" },
                  { f: "AI Photo Quotes", j: "No", s: "Limited", p: "Yes — core feature" },
                  { f: "Built-in Pro Website", j: "Limited / Extra", s: "Expensive", p: "Included + AI updates" },
                  { f: "Extra User Cost", j: "+$29 each", s: "+$245–500 each", p: "$0 on higher tiers" },
                ].map((row) => (
                  <tr key={row.f} className="border-t border-border">
                    <td className="p-4 font-semibold text-foreground">{row.f}</td>
                    <td className="p-4 text-center text-gray-700 font-medium">{row.j}</td>
                    <td className="p-4 text-center text-gray-700 font-medium">{row.s}</td>
                    <td className="p-4 text-center text-foreground font-bold bg-primary/5">{row.p}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-xs text-gray-600 mt-3">
            Competitor pricing based on publicly listed plans as of 2026. Actual costs vary by configuration.
          </p>
        </div>
      </section>


      {/* FEATURES */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Everything you need to win more work
            </h2>
            <p className="text-lg text-gray-700 font-medium max-w-3xl mx-auto">
              One simple login. No more juggling 5 different apps that do not talk to each other.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <Card key={f.title} className="border-border hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <f.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{f.title}</h3>
                  <p className="text-gray-700 font-medium text-sm leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-6 border-y border-primary/15 bg-primary/5">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <p className="text-sm md:text-base text-foreground font-semibold">
            Stop paying <span className="text-primary">$300–$500 per tech</span> for Jobber or ServiceTitan — get everything in one flat monthly price.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Simple, all-in pricing. No per-tech fees.
            </h2>
            <p className="text-lg text-gray-700 font-medium">
              Pick the plan that fits your crew. Every feature included on every tier.
            </p>
          </div>

          {/* Everything-included banner */}
          <div className="max-w-4xl mx-auto mb-8 rounded-xl border-2 border-primary/30 bg-primary/10 px-6 py-5 text-center">
            <p className="text-sm md:text-base text-foreground font-semibold leading-relaxed">
              <Check className="inline w-5 h-5 text-primary mr-1 align-text-bottom" />
              <span className="text-primary">Everything else included on ALL plans:</span>{" "}
              AI Photo-to-Quote, full CRM, marketing website + SEO, order forms + e-sign,
              floating chat alerts, persistent AI Assistant, GHL / Jobber / ServiceTitan sync,
              Google Reviews, AI Walkthrough tours, and more.
            </p>
          </div>

          {/* Annual toggle */}
          <div className="flex flex-col items-center justify-center gap-2 mb-8">
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-sm">
              <button
                type="button"
                onClick={() => setAnnual(false)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition ${!annual ? "bg-primary text-primary-foreground" : "text-gray-700"}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setAnnual(true)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition ${annual ? "bg-primary text-primary-foreground" : "text-gray-700"}`}
              >
                Annual — Save 20%
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((t) => {
              const price = annual ? t.priceAnnual : t.priceMonthly;
              return (
                <Card
                  key={t.name}
                  className={`relative flex flex-col ${
                    t.highlight
                      ? "border-2 border-primary shadow-xl lg:scale-[1.03]"
                      : "border-border"
                  }`}
                >
                  {t.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                      {annual && t.highlight ? "Best Value" : t.badge}
                    </span>
                  )}
                  <CardContent className="p-8 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-foreground mb-2">{t.name}</h3>
                    <div className="mb-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-foreground">${price}</span>
                        <span className="text-gray-700 font-medium">/mo</span>
                        {annual && (
                          <span className="text-sm text-gray-500 line-through">${t.priceMonthly}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 font-medium mt-1">
                        {annual
                          ? `billed $${t.annualBilled.toLocaleString()}/year`
                          : "billed monthly"}
                      </p>
                      {annual && (
                        <p className="text-xs font-bold text-primary mt-1">
                          Save ${t.annualSavings.toLocaleString()}/year
                        </p>
                      )}
                    </div>
                    <p className="text-gray-700 font-medium text-sm mb-4 leading-relaxed">{t.desc}</p>
                    <ul className="space-y-2 mb-6 flex-1">
                      {t.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-gray-800 font-medium">
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {t.note && (
                      <p className="text-xs font-semibold text-accent bg-accent/10 rounded-md px-2 py-1.5 mb-3 text-center">
                        {t.note}
                      </p>
                    )}
                    <div className="space-y-2">
                      <Button
                        asChild
                        size="lg"
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Link to={t.href}>
                          <Sparkles className="w-4 h-4 mr-1" /> {t.cta}
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled={loading}
                        onClick={() => subscribe(annual ? t.annualPriceId : t.monthlyPriceId)}
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Subscribe Now {annual ? "(Annual)" : "(Monthly)"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Launch special */}
          <p className="text-center text-sm text-foreground font-semibold mt-6">
            🚀 New Business Launch Special: First 3 months at <span className="text-primary">$397/mo</span> on Starter.
          </p>

          <p className="text-center text-xs text-gray-600 mt-6 max-w-2xl mx-auto">
            Tech-savvy owner or agency?{" "}
            <Link to="/template" className="text-primary font-semibold hover:underline">
              One-time source-code license available →
            </Link>
          </p>
        </div>
      </section>


      {/* ROI */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <TrendingUp className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            One extra job per month pays for the whole thing
          </h2>
          <p className="text-lg text-gray-700 font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
            The average HVAC, plumbing, or electrical job is{" "}
            <span className="font-bold text-foreground">$1,200 – $4,800</span>. ProTradeOS costs
            less than a single service call. The AI photo quote alone books jobs while you sleep.
          </p>
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
            {[
              { num: "60s", label: "AI quote turnaround" },
              { num: "28%", label: "Avg lead-to-close lift" },
              { num: "0", label: "Per-tech fees" },
            ].map((s) => (
              <Card key={s.label} className="border-primary/20">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-primary mb-1">{s.num}</div>
                  <div className="text-sm text-gray-700 font-medium">{s.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TRADES */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-3">
            Built for every home service trade
          </h2>
          <p className="text-gray-700 font-medium mb-8">
            Pick your trade — the site reskins instantly with your branding.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {trades.map((t) => (
              <Button
                asChild
                key={t.slug}
                variant="outline"
                className="border-primary/30 text-foreground hover:bg-primary hover:text-primary-foreground"
              >
                <Link to={`/services/${t.slug}`}>{t.name}</Link>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Common questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-bold text-foreground hover:text-primary">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 font-medium leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <GoogleReviewsWidget />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Ready to stop overpaying for software?
          </h2>
          <p className="text-lg text-gray-700 font-medium mb-8">
            Try the free AI Photo Quote, or book a quick demo and we will show you around.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="text-lg px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/ai-quote">
                <Camera className="w-5 h-5 mr-2" /> Try the AI Photo Quote
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto border-accent text-accent hover:bg-accent hover:text-accent-foreground"
            >
              <Link to="/contact-us">
                <Calendar className="w-5 h-5 mr-2" /> Book a 15-min Demo
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
